import type { FieldValueExtended } from "@scayla/api-client-fetch";
import { RecordStatusSlug } from "@scayla/api-client-fetch";

import type { AiTerminalStatus } from "@/store/fileUploadQueue";
import { useFileUploadQueue } from "@/store/fileUploadQueue";

import useEventStream from "./useEventStream";

const EVENT_CODE_PROGRESS = "file_classification.progress";
const EVENT_CODE_FINISHED = "file_classification.finished";
const EVENT_CODE_RECORD_FIELDS_CHANGED = "file_classification.record_fields_changed";

/** Terminal outcomes FinalizeStatus reports (backend: FinalizeOutcome). */
const OUTCOME_STATUS: Record<string, AiTerminalStatus> = {
  created: "done",
  duplicate: "duplicate",
  error: "failed",
};

/** The same terminal outcomes as seen from the record side, for the fallback below. */
const SLUG_STATUS: Partial<Record<RecordStatusSlug, AiTerminalStatus>> = {
  [RecordStatusSlug.AI_DRAFT]: "done",
  [RecordStatusSlug.AI_DUPLICATE]: "duplicate",
  [RecordStatusSlug.AI_ERROR]: "failed",
};

interface ProgressPayload {
  fileId: string;
  batchId?: string;
  stepIndex: number;
  stepTotal: number;
}

interface FinishedPayload {
  fileId: string;
  batchId?: string;
  outcome: string;
}

interface RecordFieldsChangedPayload {
  recordId: string;
  fieldValues: Record<string, FieldValueExtended>;
}

function isProgressPayload(data: unknown): data is ProgressPayload {
  return (
    !!data &&
    typeof data === "object" &&
    typeof (data as ProgressPayload).fileId === "string" &&
    typeof (data as ProgressPayload).stepIndex === "number"
  );
}

function isFinishedPayload(data: unknown): data is FinishedPayload {
  return (
    !!data &&
    typeof data === "object" &&
    typeof (data as FinishedPayload).fileId === "string" &&
    typeof (data as FinishedPayload).outcome === "string"
  );
}

function isRecordFieldsChangedPayload(data: unknown): data is RecordFieldsChangedPayload {
  return !!data && typeof data === "object" && "recordId" in data && "fieldValues" in data;
}

function terminalStatusOf(fieldValues: Record<string, FieldValueExtended>): AiTerminalStatus | undefined {
  const status = fieldValues.status;
  if (status?.kind !== "statusReference") return undefined;

  const slug = status.displayValue.slug;
  return slug === undefined ? undefined : SLUG_STATUS[slug];
}

/**
 * Feeds the upload queue's AI progress from the SSE stream (#3146): per-step pushes move the bar
 * inside a file, and `file_classification.finished` settles it. Both carry the file id and the
 * upload run's batch id, so with several runs in flight every push lands on the right one.
 *
 * A file is also settled from the general record-fields-changed push, matched by its own file record.
 * That push is the one the pipeline has always sent, so it still arrives when the classification-
 * specific one does not: dropped by the event hub under load, or never sent at all by a worker binary
 * older than #3146. Without it a finished run left the counter at 0/N with the bar already full, and a
 * run that ended on "no template found" froze the popup on the step it stopped at.
 *
 * Status toasts and grid patching stay in useFileClassificationEvents, which listens to the general
 * record-fields-changed push and is owned by ModuleView.
 */
export default function useClassificationProgressEvents() {
  const queue = useFileUploadQueue();
  const stream = useEventStream();

  const unsubscribeProgress = stream.on(EVENT_CODE_PROGRESS, (data) => {
    if (!isProgressPayload(data)) return;
    queue.applyAiProgress(data);
  });

  const unsubscribeFinished = stream.on(EVENT_CODE_FINISHED, (data) => {
    if (!isFinishedPayload(data)) return;

    const status = OUTCOME_STATUS[data.outcome];
    if (!status) return;

    queue.applyAiOutcome({ fileId: data.fileId, batchId: data.batchId, status });
  });

  const unsubscribeRecordFields = stream.on(EVENT_CODE_RECORD_FIELDS_CHANGED, (data) => {
    if (!isRecordFieldsChangedPayload(data)) return;

    const status = terminalStatusOf(data.fieldValues);
    if (!status) return;

    queue.applyAiOutcomeByRecord({ fileRecordId: data.recordId, status });
  });

  function close() {
    unsubscribeProgress();
    unsubscribeFinished();
    unsubscribeRecordFields();
    stream.close();
  }

  return { close };
}
